import { inject, Injectable } from '@angular/core';
import { FilterBy } from '@shared/sync/filter-by.model';
import { IPaginatedItemsModel } from '@shared/sync/paginated-items.model';
import { SortBy } from '@shared/sync/sort-by.model';
import { Task } from '../models/task.model';
import { TaskMapper } from '../mappers/task.mapper';
import { KfmProcessType } from '@shared/enums/process-type.enum';
import { GetTaskInPaginationResponse } from '../models/get-task-in-pagination-response.model';
import { HsBlazorService } from '@hsetu/core-services';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private readonly _serviceKey = 'kfmTask';
  private readonly _blazorService = inject(HsBlazorService);
  private readonly _mapper = new TaskMapper();

  public async getAppointmentlessTasks(
    pageSize = 10,
    pageIndex = 0,
    sortBy: SortBy | null = null,
    filterBy: FilterBy | null = null,
    showClosed = false,
    showAccomplished = false,
  ): Promise<IPaginatedItemsModel<Task>> {
    const tasksResult = await this._blazorService.invokeService(
      this._serviceKey,
      'Get',
      {
        pageIndex: pageIndex,
        pageSize: pageSize,
        showClosed: showClosed,
        showAccomplished: showAccomplished,
        sortBy: sortBy,
        filterBy: filterBy,
      },
    );

    if (tasksResult) {
      const result = tasksResult as Record<string, unknown>;
      const tasks: IPaginatedItemsModel<Task> = {
        count: Number(result['count']),
        data: (result['data'] as Task[]).map((a: Task) =>
          this._mapper.taskMap(a),
        ),
        pageIndex: Number(result['pageIndex']),
        pageSize: Number(result['pageSize']),
      };

      return tasks;
    }

    return { count: 0, data: [], pageSize: 0, pageIndex: 0 };
  }

  public async getById(id: string): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'GetByIds',
      { ids: [id] },
    );

    if (result) {
      const tasks = (result as Record<string, Task[]>)['tasks'];

      if (tasks && tasks.length > 0) {
        return this._mapper.taskMap(tasks[0]);
      }
    }

    return undefined;
  }

  public async resetTemplate(id: number): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'ResetTemplate',
      { type: id },
    );

    if (result) {
      const task = (result as Record<string, unknown>)['task'] as Task;

      if (task) {
        return this._mapper.taskMap(task);
      }
    }

    return undefined;
  }

  public async getTemplateTask(type: number): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'GetTemplate',
      { type: type },
    );

    if (result) {
      const task = (result as Record<string, unknown>)['task'] as Task;

      if (task) {
        return this._mapper.taskMap(task);
      }
    }

    return undefined;
  }

  public async getInPagination(
    taskId: string,
    from: Date,
    to: Date,
    pageSize = 10,
    showClosed = false,
    showAccomplished = false,
    sortBy: SortBy | null = null,
  ): Promise<GetTaskInPaginationResponse | undefined> {
    const taskResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetInPagination',
      {
        taskId: taskId,
        pageSize: pageSize,
        from: from,
        to: to,
        showClosed: showClosed,
        showAccomplished: showAccomplished,
        sortBy: sortBy,
      },
    );

    if (taskResult) {
      const result = taskResult as Record<string, unknown>;
      const tasks: GetTaskInPaginationResponse = {
        count: Number(result['count']),
        data: (result['data'] as Task[]).map((a: Task) =>
          this._mapper.taskMap(a),
        ),
        pageIndex: Number(result['pageIndex']),
        pageSize: Number(result['pageSize']),
        from: new Date(String(result['from'])),
        to: new Date(String(result['to'])),
      };

      return tasks;
    }

    return undefined;
  }

  public async create(customerId?: string): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Create',
      { customerId: customerId },
    );

    if (result) {
      const task = (result as Record<string, unknown>)['task'] as Task;

      if (task) {
        return this._mapper.taskMap(task);
      }
    }

    return undefined;
  }

  public async createTaskAppointment(): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'CreateWithAppointment',
    );

    if (result) {
      const task = (result as Record<string, unknown>)['task'] as Task;

      if (task) {
        return this._mapper.taskMap(task);
      }
    }

    return undefined;
  }

  public async createWithItems(
    items: { itemId: string; quantity: number }[],
  ): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'CreateWithItems',
      { taskItems: items },
    );

    if (result) {
      const task = (result as Record<string, unknown>)['task'] as Task;

      if (task) {
        return this._mapper.taskMap(task);
      }
    }

    return undefined;
  }

  public async update(value: Task): Promise<boolean> {
    const request = this._mapper.mapPutTaskRequest(value);
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Update',
      request,
    );

    return !!result;
  }

  public async delete(id: string): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Delete',
      { id: id },
    );

    const deleted = (result as Record<string, unknown>)['deleted'];
    return !!deleted;
  }

  public async convert(
    processId: string,
    processType: KfmProcessType,
  ): Promise<string | undefined> {
    const processTypeInt = processType as number;
    const request = { processId: processId, processType: processTypeInt };
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Convert',
      request,
    );

    if (result) {
      const processId = (result as Record<string, unknown>)['processId'];
      return processId ? (processId as string) : undefined;
    }

    return undefined;
  }

  public async finish(id: string): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Finish',
      { id: id },
    );

    if (result) {
      const error = (result as Record<string, unknown>)['error'];
      return !error;
    }

    return false;
  }
}
